import { debounce } from "lodash";
import { mapGetters } from "vuex";

function cloneCmData(data) {
  if (data == null) return data;
  return JSON.parse(JSON.stringify(data));
}

/**
 * Общий «скоуп» секции Content Monitoring.
 *
 * Скоуп = сопоставленные страницы + период + фильтры. Он один для всех секций,
 * но каждая секция ходит в свой эндпоинт (overview / timelines / trends /
 * mentions / feed), как и устроен CM API. Миксин даёт computed-скоуп и дёргает
 * load() при его изменении; секции реализуют только load().
 *
 * `scope` (уже сопоставленные с CM page_ids) резолвит корень вкладки и передаёт
 * пропсом — чтобы не спрашивать список страниц CM в каждой из пяти секций.
 *
 * ВАЖНО, почему следим за строкой-сигнатурой, а не за объектом через deep:
 * на главной раз в 5 секунд работает polling статусов страниц
 * (Intro-main → INTRO_UPDATE_STATUS_PAGES), который переписывает state.pages.
 * Ссылка на массив меняется, данные — нет. Deep-watch реагировал на смену
 * ссылки и перезагружал все пять секций каждые 5 секунд. Сигнатура сравнивается
 * по значению, поэтому фоновый polling больше ничего не перезагружает.
 */
export default {
  props: {
    pages: {
      type: Array,
      default: () => [],
    },
    // { pageIds, matched, unmatched, ... } из resolveScope()
    scope: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters(["getPeriod", "getCmFormat", "getCmEffectiveAdded", "getCmExcluded"]),

    cmScope() {
      return {
        period: this.getPeriod,
        format: this.getCmFormat,
        added: this.getCmEffectiveAdded,
        excluded: this.getCmExcluded,
      };
    },

    /** Скоуп без формата: смена Text/Media/All его не меняет. */
    cmScopeBase() {
      const period = this.getPeriod || {};
      return [
        (this.scope.pageIds || []).join(","),
        period.start,
        period.end,
        this.getCmEffectiveAdded.join("|"),
        this.getCmExcluded.join("|"),
      ].join("::");
    },

    /** Всё, от чего реально зависит выдача, — одной строкой. */
    cmScopeKey() {
      return [this.cmScopeBase, this.getCmFormat].join("::");
    },
  },

  watch: {
    cmScopeKey() {
      this.cmReload();
    },
  },

  methods: {
    /**
     * Запрос секции с защитой от гонки.
     *
     * Скоуп меняется быстрее, чем отвечает CM (клики по фильтрам, ввод слов),
     * поэтому ответ на устаревший запрос мог прийти последним и затереть свежие
     * данные, а заодно снять loading, пока актуальный запрос ещё в пути.
     * Применяем результат только последнего запроса.
     *
     * options.silent — не показывать loading, если уже есть данные: оставляем
     * предыдущий рендер на экране, пока не придёт новый ответ (меньше мигания
     * графиков и панелей при смене периода/слов).
     *
     * Резолвится признаком «ответ актуален» — по нему секции решают, нужно ли
     * перерисовывать графики.
     */
    cmLoad(request, apply, options = {}) {
      const base = this.cmScopeBase;
      if (this.cmCacheBase !== base) {
        this.cmCache = {};
        this.cmCacheBase = base;
      }

      const slot = [this.getCmFormat, options.cacheSuffix || ""].join("::");
      if (!options.noCache && this.cmCache[slot]) {
        apply(cloneCmData(this.cmCache[slot]));
        this.loading = false;
        this.error = "";
        this.cmSettled = true;
        return Promise.resolve(true);
      }

      const silent = !!(options.silent && this.hasData);
      const id = (this.cmRequestId += 1);
      const isStale = () => id !== this.cmRequestId;

      if (!silent) this.loading = true;
      this.error = "";

      return request()
        .then((data) => {
          if (isStale()) return false;
          if (!options.noCache) this.cmCache[slot] = cloneCmData(data);
          apply(data);
          this.cmSettled = true;
          return true;
        })
        .catch((e) => {
          if (isStale()) return false;
          this.error = e.message;
          this.cmSettled = true;
          return true;
        })
        .then((fresh) => {
          if (fresh) this.loading = false;
          return fresh;
        });
    },
  },

  created() {
    // счётчик держим вне data: он служебный, перерисовка на нём не нужна
    this.cmRequestId = 0;
    this.cmCache = {};
    this.cmCacheBase = "";
    this._cmActivatedOnce = false;
    // серия быстрых правок (слова, период, фильтры) не должна порождать
    // шторм запросов — схлопываем их в один
    this.cmReload = debounce(() => this.load(), 250);
  },

  mounted() {
    this.load();
  },

  /**
   * Вкладка CM на главной в keep-alive. Первый заход грузит mounted().
   * Возврат со списка страниц сбрасывает кэш и запрашивает заново.
   * Переключения Text/Media/All кэш не трогают.
   */
  activated() {
    if (!this._cmActivatedOnce) {
      this._cmActivatedOnce = true;
      return;
    }
    this.cmCache = {};
    this.cmCacheBase = "";
    if (this.cmReload && this.cmReload.cancel) this.cmReload.cancel();
    this.load();
  },

  beforeDestroy() {
    this.cmReload.cancel();
    // инкремент, чтобы ответ после destroy не трогал уже мёртвый инстанс
    this.cmRequestId += 1;
  },
};
